/** Левая боковая панель — sub-nav для каждой секции.
 *  - Сворачивается в полосу-иконок по кнопке внизу.
 *  - Состояние свёрнутости в localStorage (`ritm.sidebar.collapsed`).
 *  - Активный пункт определяется по `location.pathname`. */
import { For, Show, createSignal, type Component } from "solid-js";
import { A, useLocation } from "@solidjs/router";
import { PanelLeftClose, PanelLeftOpen } from "lucide-solid";
import { SIDEBAR_BY_SECTION, sectionFromPath } from "~/lib/sidebarConfig";
import { useI18n } from "~/i18n";

const COLLAPSED_KEY = "ritm.sidebar.collapsed";

function readInitialCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

export const Sidebar: Component = () => {
  const location = useLocation();
  const { t } = useI18n();
  const [collapsed, setCollapsed] = createSignal(readInitialCollapsed());

  const section = () => sectionFromPath(location.pathname);
  const items = () => (section() ? SIDEBAR_BY_SECTION[section()!] : []);

  const isActive = (path: string) => {
    const p = location.pathname;
    return p === path || p.startsWith(path + "/");
  };

  const toggle = () => {
    const next = !collapsed();
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
    } catch {
      /* no-op */
    }
  };

  return (
    <Show when={items().length > 0}>
      <aside
        class="flex flex-shrink-0 flex-col border-r border-border bg-surface-2 transition-[width] duration-200 ease-out"
        classList={{
          "w-[56px]": collapsed(),
          "w-[var(--nav-w)]": !collapsed(),
        }}
      >
        <nav class="flex-1 space-y-1 overflow-y-auto px-2 pb-2 pt-6">
          <For each={items()}>
            {(item) => {
              const active = () => isActive(item.path);
              const label = () => t(item.labelKey);
              return (
                <A
                  href={item.path}
                  title={collapsed() ? label() : undefined}
                  class="flex h-9 w-full items-center gap-2.5 rounded-md border text-base no-underline transition-colors"
                  classList={{
                    "border-border bg-surface text-ink font-semibold shadow-sm":
                      active(),
                    "border-transparent text-ink-2 hover:bg-surface-hover font-medium":
                      !active(),
                    "justify-center px-0": collapsed(),
                    "px-3 text-left": !collapsed(),
                  }}
                >
                  <item.Icon
                    size={16}
                    class={active() ? "text-primary" : "text-ink-muted"}
                  />
                  <Show when={!collapsed()}>
                    <span class="flex-1 truncate">{label()}</span>
                  </Show>
                </A>
              );
            }}
          </For>
        </nav>

        <div class="border-t border-border p-2">
          <button
            type="button"
            onClick={toggle}
            class="flex h-9 w-full items-center gap-2 rounded-md text-base font-medium text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
            classList={{
              "justify-center px-0": collapsed(),
              "px-3": !collapsed(),
            }}
            title={collapsed() ? t("sidebar.expand") : t("sidebar.collapse")}
            aria-label={collapsed() ? t("sidebar.expand") : t("sidebar.collapse")}
          >
            <Show when={collapsed()} fallback={<PanelLeftClose size={16} />}>
              <PanelLeftOpen size={16} />
            </Show>
            <Show when={!collapsed()}>
              <span class="flex-1 text-left">{t("sidebar.collapse")}</span>
            </Show>
          </button>
        </div>
      </aside>
    </Show>
  );
};
